import { ENV } from "@/config/env";
import { companyRequest } from "@/services/companyRequest";
import sessionManager from "@/services/sessionManager";
import { createUuid } from "@/utils/uuid";

const aiBasePath = (companyId, facilityId) =>
  `/api/v2/facilities/${encodeURIComponent(companyId)}/${encodeURIComponent(facilityId)}/ai`;

const unwrap = (response) => response?.data ?? response;

const parseSseBlock = (block) => {
  const lines = block
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .filter((line) => line && !line.startsWith(":"));

  if (!lines.length) return null;

  const eventLine = lines.find((line) => line.startsWith("event:"));
  const eventName = eventLine ? eventLine.slice(6).trim() : "";
  const data = lines
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).trim())
    .join("\n");

  if (!data) return null;

  try {
    const parsed = JSON.parse(data);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return { event: parsed.event || eventName, ...parsed };
    }
    return { event: eventName, data: parsed };
  } catch (error) {
    return { event: eventName, data };
  }
};

const readErrorDetail = async (response) => {
  try {
    const payload = await response.json();
    return payload?.detail || payload?.message || payload?.error;
  } catch (error) {
    return "";
  }
};

export const listFacilityAiConversations = async (companyId, facilityId) => {
  const response = await companyRequest.get(`${aiBasePath(companyId, facilityId)}/conversations/`);
  return unwrap(response);
};

export const getFacilityAiConversationMessages = async (companyId, facilityId, conversationId) => {
  const response = await companyRequest.get(
    `${aiBasePath(companyId, facilityId)}/conversations/${conversationId}/messages/`
  );
  return unwrap(response);
};

export const updateFacilityAiConversation = async (companyId, facilityId, conversationId, payload) => {
  const response = await companyRequest.patch(
    `${aiBasePath(companyId, facilityId)}/conversations/${conversationId}/`,
    payload
  );
  return unwrap(response);
};

export const deleteFacilityAiConversation = async (companyId, facilityId, conversationId) => {
  await companyRequest.delete(`${aiBasePath(companyId, facilityId)}/conversations/${conversationId}/`);
};

export const streamFacilityAiChat = async ({
  companyId,
  facilityId,
  conversationId,
  message,
  signal,
  onEvent,
}) => {
  const { access } = sessionManager.getSessionSnapshot();
  const payload = {
    message,
    company_id: Number(companyId),
    facility_id: Number(facilityId),
  };

  if (conversationId) {
    payload.conversation_id = conversationId;
  }

  const response = await fetch(`${ENV.backendUrl}${aiBasePath(companyId, facilityId)}/chat/stream/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      Authorization: access ? `Bearer ${access}` : "",
      "X-Trace-Id": createUuid(),
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok || !response.body) {
    const detail = await readErrorDetail(response);
    throw new Error(detail || `AI request failed with status ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    // Keep the raw buffer until a complete LF/CRLF separator is present so a
    // separator split across ReadableStream chunks cannot drop an event.
    const blocks = buffer.split(/\r?\n\r?\n/);
    buffer = blocks.pop() || "";

    blocks.forEach((block) => {
      const event = parseSseBlock(block);
      if (event) onEvent(event);
    });
  }

  buffer += decoder.decode();
  if (buffer.trim()) {
    const event = parseSseBlock(buffer);
    if (event) onEvent(event);
  }
};
